import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useMemo, useState } from "react"
import { toast } from "sonner"

import { AdminPageHeader } from "@/components/AdminPageHeader"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { api } from "@/lib/client"
import { formatDateLabel } from "@/lib/format"
import { queryKeys } from "@/lib/queryKeys"
import { ADMIN_TABLE_CLASSNAME, ADMIN_TABLE_HEAD_TH } from "@/lib/table"

export function LotsPage() {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [productId, setProductId] = useState("")
  const [lotCode, setLotCode] = useState("")
  const [qty, setQty] = useState("0")
  const [expiresOn, setExpiresOn] = useState("")
  const lots = useQuery({ queryFn: () => api.lots(), queryKey: queryKeys.lots })
  const products = useQuery({
    queryFn: () => api.products({}),
    queryKey: queryKeys.products({}),
  })
  const rows = useMemo(
    () => [...(lots.data?.lots ?? [])].sort((a, b) => (a.expiresOn ?? "").localeCompare(b.expiresOn ?? "")),
    [lots.data],
  )

  const mutation = useMutation({
    mutationFn: () =>
      api.createLot({
        expiresOn: expiresOn || undefined,
        lotCode,
        productId: Number(productId),
        qty: Number(qty),
      }),
    onError: (err) => toast.error(err.message),
    onSuccess: async () => {
      toast.success("Lot received")
      setOpen(false)
      setLotCode("")
      setQty("0")
      await queryClient.invalidateQueries({ queryKey: queryKeys.lots })
      await queryClient.invalidateQueries({ queryKey: queryKeys.stock })
    },
  })

  return (
    <div>
      <AdminPageHeader description="Lot-tracked receipts. Creating a lot posts a stock receipt." title="Lots">
        <Button onClick={() => setOpen(true)} size="sm" type="button">
          Receive lot
        </Button>
      </AdminPageHeader>
      <div className="overflow-hidden rounded-lg border border-border">
        <Table className={ADMIN_TABLE_CLASSNAME}>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className={ADMIN_TABLE_HEAD_TH}>Lot</TableHead>
              <TableHead className={ADMIN_TABLE_HEAD_TH}>Product</TableHead>
              <TableHead className={`${ADMIN_TABLE_HEAD_TH} text-right`}>Remaining</TableHead>
              <TableHead className={ADMIN_TABLE_HEAD_TH}>Expires</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {lots.isPending ? (
              <TableRow>
                <TableCell className="text-muted-foreground" colSpan={4}>
                  Loading…
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell className="text-muted-foreground" colSpan={4}>
                  No lots yet.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((lot) => (
                <TableRow key={lot.id}>
                  <TableCell className="font-medium">{lot.lotCode}</TableCell>
                  <TableCell>
                    {lot.sku}
                  </TableCell>
                  <TableCell className="text-right">{lot.qtyRemaining}</TableCell>
                  <TableCell className="text-muted-foreground">{formatDateLabel(lot.expiresOn)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      <Dialog onOpenChange={setOpen} open={open}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Receive lot</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>Product</Label>
              <Select onValueChange={setProductId} value={productId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select product" />
                </SelectTrigger>
                <SelectContent>
                  {(products.data?.products ?? []).map((p) => (
                    <SelectItem key={p.id} value={String(p.id)}>
                      {p.name} ({p.sku})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="lotCode">Lot code</Label>
              <Input id="lotCode" onChange={(e) => setLotCode(e.target.value)} value={lotCode} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lotQty">Quantity</Label>
              <Input id="lotQty" onChange={(e) => setQty(e.target.value)} type="number" value={qty} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="exp">Expires</Label>
              <Input id="exp" onChange={(e) => setExpiresOn(e.target.value)} type="date" value={expiresOn} />
            </div>
          </div>
          <DialogFooter>
            <Button
              disabled={!lotCode || !productId || mutation.isPending}
              onClick={() => mutation.mutate()}
              type="button"
            >
              Receive
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
